import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaChartLine } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Timeline visual de continuaciones mensuales · Fit Evolution.
 * Muestra evolución de peso, sensación, y cardio recomendado mes a mes.
 * Motivador y ayuda a la clienta a ver su progreso a largo plazo.
 */
export default function ContinuationHistoryCard({ email }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/personalized-plans/fit-evolution/continuation-history/${encodeURIComponent(email)}`,
          { timeout: 4000 }
        );
        setEntries(data.entries || []);
      } catch (e) { /* silencioso · si falla no renderizamos nada */ }
      finally { setLoading(false); }
    })();
  }, [email]);

  if (loading || entries.length === 0) return null;

  // Deduplicar por mes (una sola entrada por mes-año)
  const byMonth = new Map();
  entries.forEach((e) => {
    if (!e.date) return;
    const d = new Date(e.date);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    // Mantener la última del mes (más reciente)
    byMonth.set(key, e);
  });
  const rows = Array.from(byMonth.values()).sort((a, b) => new Date(b.date) - new Date(a.date));

  const monthLabel = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  };

  const feelingBadge = (f) => {
    if (f === 'positivo') return { label: '😊 Voy bien', color: '#34D399', bg: 'rgba(52, 211, 153, 0.15)', border: 'rgba(52, 211, 153, 0.35)' };
    if (f === 'negativo') return { label: '😞 No bien', color: '#F87171', bg: 'rgba(248, 113, 113, 0.15)', border: 'rgba(248, 113, 113, 0.35)' };
    if (f === 'sin_cambios') return { label: '😐 Sin cambios', color: '#FBBF24', bg: 'rgba(251, 191, 36, 0.15)', border: 'rgba(251, 191, 36, 0.35)' };
    return null;
  };

  const wcColor = (wc) => {
    if (wc == null || wc === 0) return '#94A3B8';
    return wc < 0 ? '#34D399' : '#F87171';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-8"
      data-testid="continuation-history-card"
    >
      <div
        className="rounded-2xl p-6 backdrop-blur-xl border"
        style={{ background: 'rgba(15, 23, 42, 0.55)', borderColor: 'rgba(139, 92, 246, 0.25)' }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #A78BFA, #6D28D9)' }}>
            <FaChartLine className="text-white text-xl" />
          </div>
          <div>
            <h3 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Tu evolución</h3>
            <p className="text-xs text-gray-400">{rows.length} {rows.length === 1 ? 'actualización' : 'actualizaciones'} mensual{rows.length === 1 ? '' : 'es'}</p>
          </div>
        </div>

        <div className="space-y-3">
          {rows.map((e, idx) => {
            const fb = feelingBadge(e.result_feeling);
            const wc = e.weight_change;
            const wcSign = wc == null ? null : (wc > 0 ? '+' : '');
            return (
              <div
                key={e.id || idx}
                data-testid={`history-entry-${idx}`}
                className="rounded-xl p-4"
                style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)' }}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-white font-bold capitalize text-sm">{monthLabel(e.date)}</p>
                  {fb && (
                    <span
                      className="px-2.5 py-0.5 rounded-full text-[11px] font-bold"
                      style={{ background: fb.bg, color: fb.color, border: `1px solid ${fb.border}` }}
                    >
                      {fb.label}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-2">
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-0.5">Peso</p>
                    <p className="text-white font-black text-lg">{e.weight ?? '—'} <span className="text-xs text-gray-500 font-normal">kg</span></p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-0.5">Cambio</p>
                    <p className="font-black text-lg" style={{ color: wcColor(wc) }}>
                      {wc == null ? '—' : `${wcSign}${wc} kg`}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-0.5">Cardio</p>
                    <p className="text-white font-black text-lg">{e.cardio_sessions ?? '—'} <span className="text-xs text-gray-500 font-normal">sesiones</span></p>
                  </div>
                </div>

                {e.cardio_format && (
                  <p className="text-xs text-gray-400">{e.cardio_format}</p>
                )}
                {e.cardio_recommendation && (
                  <p className="text-xs text-amber-200/90 mt-1.5 italic border-t border-white/5 pt-1.5">💡 {e.cardio_recommendation}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
